import { type FC, type ReactNode, useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ReCAPTCHA from 'react-google-recaptcha';
import styled from 'styled-components';
import {
  RECAPTCHA_CHALLANGE_KEY,
  RECAPTCHA_SCORE_KEY,
  RECAPTCHA_API_KEY,
  RECAPTCHA_PROJECT_ID,
} from '@/constants/env';

const CaptchaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: ${({ theme }) => theme.color.ui5 || '#f5f5f5'};
  padding: 2rem;
`;

const CaptchaBox = styled.div`
  background: ${({ theme }) => theme.color.ui7};
  border-radius: 16px;
  padding: 3rem 2rem;
  box-shadow: ${({ theme }) => theme.shadows?.shadow1 || '0 20px 40px rgba(0, 0, 0, 0.1)'};
  text-align: center;
  max-width: 400px;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.color.ui4};
`;

const CaptchaTitle = styled.h2`
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.color.s1};
  ${({ theme }) => theme.font?.t1 || 'font-size: 1.5rem; font-weight: 600;'};
`;

const CaptchaSubtitle = styled.p`
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.color.ui3};
  ${({ theme }) => theme.font?.t2 || 'font-size: 1rem; line-height: 1.5;'};
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.color.ui3};
  font-size: 0.9rem;
  margin-top: 1rem;
`;

const ReCaptchaGuard: FC<{ children: ReactNode }> = ({ children }) => {
  const [isVerified, setIsVerified] = useState(
    () => localStorage.getItem('captchaSuccess') === 'true',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha || isVerified) return;

    setIsLoading(true);
    try {
      const captchaToken = await executeRecaptcha('app_access');

      // Validate with Enterprise API (same pattern as your other app)
      const response = await fetch(
        `https://recaptchaenterprise.googleapis.com/v1/projects/${RECAPTCHA_PROJECT_ID}/assessments?key=${RECAPTCHA_API_KEY}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: {
              token: captchaToken,
              expectedAction: 'app_access',
              siteKey: RECAPTCHA_SCORE_KEY,
            },
          }),
        },
      );

      const { riskAnalysis } = await response.json();

      if (riskAnalysis.score > 0.5) {
        localStorage.setItem('captchaSuccess', 'true');
        setIsVerified(true);
      } else {
        // Score too low, show v2 fallback
        console.warn('reCaptcha v3 score too low:', riskAnalysis.score);
        setShowCaptcha(true);
      }
    } catch (error) {
      console.error('reCaptcha v3 verification failed:', error);
      // Show v2 fallback on error
      setShowCaptcha(true);
    } finally {
      setIsLoading(false);
    }
  }, [executeRecaptcha, isVerified]);

  useEffect(() => {
    if (!isVerified) {
      // Try v3 first, then show v2 if needed
      handleReCaptchaVerify();
    }
  }, [handleReCaptchaVerify, isVerified]);

  const onCaptchaChange = (value: string | null) => {
    if (value) {
      localStorage.setItem('captchaSuccess', 'true');
      setIsVerified(true);
    }
  };

  // If verified, render the app immediately
  if (isVerified) {
    return <>{children}</>;
  }

  // Show captcha verification UI
  return (
    <CaptchaContainer>
      <CaptchaBox>
        <CaptchaTitle>Security Verification</CaptchaTitle>
        <CaptchaSubtitle>
          Please complete the security check to access the application.
        </CaptchaSubtitle>

        {isLoading ? (
          <LoadingText>Verifying security...</LoadingText>
        ) : showCaptcha ? (
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <ReCAPTCHA sitekey={RECAPTCHA_CHALLANGE_KEY} onChange={onCaptchaChange} theme="light" />
          </div>
        ) : (
          <LoadingText>Initializing security check...</LoadingText>
        )}
      </CaptchaBox>
    </CaptchaContainer>
  );
};

export default ReCaptchaGuard;
